import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Building2, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import LegalFooter from "@/components/LegalFooter";
import { toast } from "sonner";

export default function Register() {
  const { registerCompany, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("company"); // company | join
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);

  const [company, setCompany] = useState({ company_name: "", address: "", postcode: "", admin_name: "", admin_email: "", admin_password: "" });
  const [join, setJoin] = useState({ name: "", email: "", password: "", role: "inspector", company_code: "" });

  const setC = (k) => (e) => setCompany((f) => ({ ...f, [k]: e.target.value }));
  const setJ = (k) => (e) => setJoin((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "company") {
        if (!company.company_name.trim() || !company.admin_name.trim() || !company.admin_email.trim() || !company.admin_password) {
          setError("Please complete all required fields."); setLoading(false); return;
        }
        if (!accepted) {
          setError("Please accept the Terms of Use and Privacy Policy to continue."); setLoading(false); return;
        }
        await registerCompany({ ...company, accept_legal: true });
        toast.success("Company created — welcome to FD Check");
      } else {
        if (!join.name.trim() || !join.email.trim() || !join.password || !join.company_code.trim()) {
          setError("Please complete all required fields."); setLoading(false); return;
        }
        await register({ ...join, email: join.email.trim().toLowerCase(), company_code: join.company_code.trim() });
        toast.success("Account created");
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(apiError(err, "Could not create your account."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-white px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center">
          <img src="/fdcheck-logo.jpg" alt="FD Check" className="h-20 w-auto object-contain" />
          <p className="mt-2 text-sm text-slate-500">Create your account</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-6 grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1">
            <button onClick={() => setMode("company")} className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition-colors ${mode === "company" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`} data-testid="register-mode-company">
              <Building2 className="h-4 w-4" /> New company
            </button>
            <button onClick={() => setMode("join")} className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition-colors ${mode === "join" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`} data-testid="register-mode-join">
              <UserPlus className="h-4 w-4" /> Join a company
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "company" ? (
              <>
                <Field label="Company name *"><Input value={company.company_name} onChange={setC("company_name")} data-testid="reg-company-name" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Address"><Input value={company.address} onChange={setC("address")} data-testid="reg-company-address" /></Field>
                  <Field label="Postcode"><Input value={company.postcode} onChange={setC("postcode")} data-testid="reg-company-postcode" /></Field>
                </div>
                <Field label="Your name *"><Input value={company.admin_name} onChange={setC("admin_name")} data-testid="reg-admin-name" /></Field>
                <Field label="Email *"><Input type="email" value={company.admin_email} onChange={setC("admin_email")} data-testid="reg-admin-email" /></Field>
                <Field label="Password * (min 6)"><Input type="password" value={company.admin_password} onChange={setC("admin_password")} data-testid="reg-admin-password" /></Field>
              </>
            ) : (
              <>
                <Field label="Company code *"><Input value={join.company_code} onChange={setJ("company_code")} placeholder="e.g. FDCHECK-MAIN" data-testid="reg-company-code" /></Field>
                <Field label="Your name *"><Input value={join.name} onChange={setJ("name")} data-testid="reg-join-name" /></Field>
                <Field label="Email *"><Input type="email" value={join.email} onChange={setJ("email")} data-testid="reg-join-email" /></Field>
                <Field label="Password * (min 6)"><Input type="password" value={join.password} onChange={setJ("password")} data-testid="reg-join-password" /></Field>
                <Field label="Role">
                  <Select value={join.role} onValueChange={(v) => setJoin((f) => ({ ...f, role: v }))}>
                    <SelectTrigger data-testid="reg-join-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inspector">Inspector</SelectItem>
                      <SelectItem value="engineer">Engineer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </>
            )}

            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-error">{error}</p>}

            {mode === "company" && (
              <label className="flex items-start gap-2.5 text-sm text-slate-600" data-testid="register-accept-label">
                <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" data-testid="register-accept-legal" />
                <span>
                  I agree to the{" "}
                  <Link to="/legal/terms" target="_blank" className="font-medium text-primary hover:underline">Terms of Use</Link>{" "}
                  and{" "}
                  <Link to="/legal/privacy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link>.
                </span>
              </label>
            )}

            <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="register-submit">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {mode === "company" ? "Create company" : "Join company"}
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-slate-500">
            Already have an account? <Link to="/login" className="font-medium text-primary hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
        </div>
        <LegalFooter className="mt-6" />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
